import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { HamMenuItem } from '@modeso/aumico-components';
import { I18NextPipe } from 'angular-i18next';
import {
    AdjustmentFilterCounts,
    AdjustmentResultEffect,
    AdjustmentFilterKey,
    AdjustmentResultRows,
    AdjustmentRow,
    AdjustmentSort,
    AdjustmentSortKey,
    AdjustmentSource,
    AdjustmentTransferMethod,
    AdjustmentTransferOptions,
} from '../../types/closing-adjustments.types';
import { SortDirection, ariaSortOf, directionOf, nextSort } from '../sort-header/sort-header.types';

/**
 * REV-21370 — the closing-wide Korrekturbuchungen register (Pure View). Contract:
 * `e2e-lab/closing-adjustments.lab.spec.ts`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * **Dumb by construction** (`conventions.md` §ABCP-FE, ADR-0015 §12): no store, no HTTP, no
 * filtering, no sorting, no arithmetic. Filter, sort and the chosen fiscal year are CONTROLLED — they
 * arrive as inputs and leave as events, and the smart host (REV-21375) owns them.
 *
 * **Not one row, not one figure, not one state comes into existence here** (AC12). The three result
 * figures are computed by the backend's pure function (§11) and arrive as ready numbers; the counts
 * span the whole closing-year and the view adds nothing up.
 *
 * ⚠️ **NOTHING here can set `Gebucht`** (§2). There is no control for it, and there must not be. The
 * user sees a correction become booked when the ERP reports it back. The one place a person chooses
 * anything about it is the ambiguity picker on the DETAIL surface, and it only names which of the
 * lines the import contained is the proof.
 *
 * ⚠️ **NO agent surface** (§8): no confidence column, no reasoning trail, no proposal state, no
 * accept and no reject control. The `Quelle` column IS here — it is the connection point, so a later
 * second source is a data change rather than a change to this table's head, its sorting, its filter,
 * the detail surface, the translations and the tests of two components. The lab spec asserts every one
 * of those absences.
 *
 * ⚠️ **THE SURFACE NAMES NO ACCOUNTING SYSTEM** (§3a). aumico is connected to four of them. Where a
 * label carries a system name it comes from `transferOptions.connectedName` — the mandate's own
 * connection — and where nothing is connected the surface says so instead of inventing one. The
 * prototype writes "bexio" because its demo company sits there; a hard-coded name here would be false
 * for every Abacus, Topal or D365 mandate.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 */

/** The three pills, in render order. Always all three — never hidden at count 0. */
// Prototype order: Alle, Offen, Übertragen, Gebucht — the three states in the order a correction
// moves through them, after the catch-all.
// ── 5 · `Verworfen` is a pill, not a state (prototype change 03.09.2026) ────────────────────────
//
// It sits last because it is a different KIND of question from the four before it: those narrow by how
// far a booking travelled, this one shows the ones taken out of circulation. A withdrawn booking keeps
// the state it was withdrawn from, so it cannot be a fifth member of the state filter.
const FILTER_KEYS: AdjustmentFilterKey[] = ['all', 'released', 'transferred', 'booked', 'discarded'];

const FILTER_LABEL_KEYS: Record<AdjustmentFilterKey, string> = {
    all: 'abcp_adjustments_filter_all',
    released: 'abcp_adjustments_filter_released',
    transferred: 'abcp_adjustments_filter_transferred',
    booked: 'abcp_adjustments_filter_booked',
    discarded: 'abcp_adjustments_filter_discarded',
};

/** The six sortable columns, in render order. The 7th (the row menu) carries no header and no sort. */
// The prototype's order and its seven columns: Buchungstext · Soll · Haben · Betrag · P&L ·
// Quelle · Status. `Status` sits LAST there, not first.
const COLUMNS: AdjustmentSortKey[] = ['text', 'debit', 'credit', 'amount', 'pnl', 'source', 'state'];

const COLUMN_LABEL_KEYS: Record<AdjustmentSortKey, string> = {
    text: 'abcp_adjustments_col_text',
    debit: 'abcp_adjustments_col_debit',
    credit: 'abcp_adjustments_col_credit',
    amount: 'abcp_adjustments_col_amount',
    pnl: 'abcp_adjustments_col_pnl',
    source: 'abcp_adjustments_col_source',
    state: 'abcp_adjustments_col_state',
};

/** What the `P&L` cell prints. The words live in the tooltip, not the cell. */
const RESULT_EFFECT_SIGNS: Record<AdjustmentResultEffect, string> = {
    increase: '+',
    decrease: '−',
    none: '—',
    // NOT the same glyph as `none`, and that is the whole point of V2/AC12: `—` says "does not touch
    // the result", `?` says "nobody classified one of these accounts, so this booking is left out of
    // the total". Rendering both as `—` made an incomplete number look complete.
    excluded: '?',
};

/** The three readings of the `P&L` column, as the prototype words them — tooltip and a11y. */
const RESULT_EFFECT_LABEL_KEYS: Record<AdjustmentResultEffect, string> = {
    increase: 'abcp_adjustments_effect_increase',
    decrease: 'abcp_adjustments_effect_decrease',
    none: 'abcp_adjustments_effect_none',
    excluded: 'abcp_adjustments_result_excluded_hint',
};

const SOURCE_LABEL_KEYS: Record<AdjustmentSource, string> = {
    user: 'abcp_adjustments_source_user',
    agent: 'abcp_adjustments_source_agent',
    priorYear: 'abcp_adjustments_source_prior_year',
};

/** The three transfer targets, in the prototype's order. Each renders a title AND a reason. */
const TRANSFER_TARGETS: readonly AdjustmentTransferMethod[] = ['erpApi', 'markedManual'];

const TRANSFER_TITLE_KEYS: Record<AdjustmentTransferMethod, string> = {
    erpApi: 'abcp_adjustments_transfer_erp',
    markedManual: 'abcp_adjustments_transfer_manual',
};

const TRANSFER_SUBTITLE_KEYS: Record<AdjustmentTransferMethod, string> = {
    erpApi: 'abcp_adjustments_transfer_erp_hint',
    markedManual: 'abcp_adjustments_transfer_manual_hint',
};

@Component({
    selector: 'abcp-closing-adjustments',
    templateUrl: './closing-adjustments.component.html',
    styleUrls: ['./closing-adjustments.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpClosingAdjustmentsComponent {
    /**
     * The i18n pipe as a dependency, the same way `abcp-account-workspace` takes it.
     *
     * Needed because an `<option>` may not contain elements, so the `· aktuell` suffix cannot
     * be assembled in the template — it has to arrive as one string.
     */
    constructor(private readonly i18next: I18NextPipe) {}

    /** Already filtered, searched and sorted by the host. The view renders them in order. */
    @Input() adjustments: AdjustmentRow[] = [];

    /**
     * All three counts, always rendered.
     *
     * ⚠️ `all` is NOT `open + booked` — a `transferred` correction is in `all` and in neither of the
     * others (§2). The view renders the three numbers and adds nothing up, which is what stops it
     * inventing a fourth that looks plausible and is wrong.
     */
    @Input() counts: AdjustmentFilterCounts = { all: 0, released: 0, transferred: 0, booked: 0, discarded: 0 };

    @Input() activeFilter: AdjustmentFilterKey = 'all';

    /** `null` = unsorted; every header then shows the neutral indicator. */
    @Input() sort: AdjustmentSort | null = null;

    /** The current fiscal year and the two before it (§10), newest first. */
    @Input() fiscalYears: number[] = [];

    @Input() selectedFiscalYear: number | null = null;

    /**
     * A PRIOR year — the whole surface is then read-only (§10).
     *
     * Served rather than derived from `selectedFiscalYear`, because "which year is current" is the
     * backend's fact and the view must not hold a second copy of it. It is a business statement, not a
     * UI saving: a closed fiscal year is not corrected after the fact, it is cleaned up in the current
     * one — and with controls this view would be dangerous.
     */
    @Input() readOnly = false;

    /** The three derived figures plus the list total. Computed upstream (§11) — never here. */
    @Input() result: AdjustmentResultRows | null = null;

    /**
     * What the transfer control may offer (§3a rule 3).
     *
     * The default deliberately says NOTHING is connected: a view that defaulted to "can write" would
     * offer `Direkt ans ERP senden` for a mandate whose system cannot take a posting, and the failure
     * would only show after the user pressed it.
     */
    @Input() transferOptions: AdjustmentTransferOptions = {
        canWriteToErp: false,
        reasonKey: null,
        connectedName: null,
    };

    /**
     * The search TERM, not a search field: the input box lives in the workspace header, which owns
     * the term per tab (REV-21360). `adjustments` arrives already searched.
     *
     * Kept as a seam so the register can report the hit count above the list (AC3) without the host
     * having to move the field.
     */
    @Input() searchTerm = '';

    /** How many rows the search matched, when one is active. `null` = no search. */
    @Input() searchHits: number | null = null;

    @Input() loading = false;
    @Input() error = false;

    @Output() filterChange = new EventEmitter<AdjustmentFilterKey>();
    /** `null` = back to the server's order (newest first). */
    @Output() sortChange = new EventEmitter<AdjustmentSort | null>();
    @Output() fiscalYearChange = new EventEmitter<number>();
    @Output() searchReset = new EventEmitter<void>();

    /** A row click opens the DETAIL surface — a navigation the host performs (AC8). */
    @Output() adjustmentOpen = new EventEmitter<string>();

    /** `Neue Buchung`. The modal is a sibling component the HOST renders (AC5). */
    @Output() createRequested = new EventEmitter<void>();

    /** `Übertragen (n)` with one of three targets (AC7). */
    @Output() transferRequested = new EventEmitter<AdjustmentTransferMethod>();

    /** The user asked to try again after an error (AC11). */
    @Output() retryRequested = new EventEmitter<void>();

    readonly filterKeys = FILTER_KEYS;
    readonly columns = COLUMNS;
    readonly transferTargets = TRANSFER_TARGETS;
    readonly skeletonRows = [1, 2, 3, 4, 5, 6];


    get isEmpty(): boolean {
        return this.adjustments.length === 0;
    }

    /**
     * Placeholders only while there is nothing to show yet. A reload keeps the rows it has until the new
     * ones arrive: placeholders over old rows read as two states at once, and swapping the rows for
     * placeholders on every refresh (a poll, a reload after a write) makes the table flash.
     */
    get showSkeleton(): boolean {
        return this.loading && this.adjustments.length === 0;
    }

    /** The sortable columns — every column there is. The row menu and its column are gone (03.09.2026). */
    get columnCount(): number {
        return this.columns.length;
    }

    /** How many corrections `Übertragen (n)` would take. From the COUNTS, never counted here. */
    get openCount(): number {
        return this.counts.released;
    }

    /** `Übertragen` is inactive when there is nothing open (AC7). */
    get canTransfer(): boolean {
        return !this.readOnly && this.openCount > 0;
    }

    /**
     * Whether a search is narrowing the list.
     *
     * "No search hit" is a DIFFERENT state from "empty" and is worded differently (AC11) — a user who
     * typed something needs to know that the filter, not the closing, is why the table is bare.
     */
    get isSearching(): boolean {
        return this.searchTerm.trim().length > 0;
    }

    get hasNegativeResult(): boolean {
        return (this.result?.resultAfterMinor ?? 0) < 0;
    }

    /** `+` or `−` before the impact figure. The sign is SHOWN, because the direction is the point. */
    get impactSign(): string {
        return (this.result?.impactMinor ?? 0) >= 0 ? '+' : '−';
    }

    /** The impact as a magnitude — the sign is rendered separately, as the prototype does. */
    get impactMagnitudeMinor(): number {
        return Math.abs(this.result?.impactMinor ?? 0);
    }

    filterLabelKey(key: AdjustmentFilterKey): string {
        return FILTER_LABEL_KEYS[key];
    }

    filterCount(key: AdjustmentFilterKey): number {
        return this.counts[key];
    }

    columnLabelKey(key: AdjustmentSortKey): string {
        return COLUMN_LABEL_KEYS[key];
    }

    sourceLabelKey(source: AdjustmentSource): string {
        return SOURCE_LABEL_KEYS[source];
    }

    /** The `P&L` cell's label key for one row — the tooltip, and the accessible reading. */
    resultEffectKey(effect: AdjustmentResultEffect): string {
        return RESULT_EFFECT_LABEL_KEYS[effect];
    }

    /**
     * The `P&L` cell itself: the DIRECTION, nothing else.
     *
     * A plus, a minus, or an em dash. The amount belongs to `Betrag` and repeating it here would
     * say nothing the row does not already say two cells to the left -- this column answers one
     * question, which is whether the year result moves and which way.
     */
    /**
     * What the `Quelle` cell prints: a person's INITIALS, or the agent's label.
     *
     * ⚠️ Returns either an i18n KEY or a literal, and the template pipes both through `i18next` —
     * which passes an unknown key through unchanged. That is deliberate: initials are data and must
     * not be translated, the agent marking is a word and must be. One cell, two kinds of content, and
     * no second template branch to keep in step.
     *
     * The initials come from whoever LAST stood behind the booking: the editor if there was an edit,
     * otherwise the person who entered it. That is the same rule the source value itself follows.
     */
    sourceDisplay(row: AdjustmentRow): string {
        if (row.source !== 'user') {
            return SOURCE_LABEL_KEYS[row.source];
        }

        return AbcpClosingAdjustmentsComponent.initialsOf(row.editedBy ?? row.enteredBy ?? null)
            ?? SOURCE_LABEL_KEYS.user;
    }

    /** The full name behind the initials, so the cell stays readable without being wide. */
    sourceTitle(row: AdjustmentRow): string {
        return row.source === 'user' ? (row.editedBy ?? row.enteredBy ?? '') : '';
    }

    /**
     * `Anna Meier` → `AM`. `null` when there is no name to shorten.
     *
     * At most two letters, from the first and last word: a three-part name would otherwise print three
     * letters and break the column's width. Returns `null` rather than `''` so the caller can fall
     * back to the label instead of rendering an empty cell.
     */
    private static initialsOf(name: string | null): string | null {
        const parts = (name ?? '').trim().split(/\s+/).filter((part) => part.length > 0);
        if (parts.length === 0) {
            return null;
        }

        const first = parts[0]!.charAt(0);
        const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : '';

        return (first + last).toUpperCase();
    }

    resultEffectSign(effect: AdjustmentResultEffect): string {
        return RESULT_EFFECT_SIGNS[effect];
    }

    transferTitleKey(method: AdjustmentTransferMethod): string {
        return TRANSFER_TITLE_KEYS[method];
    }

    transferSubtitleKey(method: AdjustmentTransferMethod): string {
        return TRANSFER_SUBTITLE_KEYS[method];
    }

    /**
     * Whether a transfer target may be chosen.
     *
     * ⚠️ `markedManual` is ALWAYS available — that is precisely what it exists for (§3).
     * Only `erpApi` is a question, and the answer is the backend's queried capability, never a constant
     * in this file.
     */
    isTransferTargetEnabled(method: AdjustmentTransferMethod): boolean {
        return method !== 'erpApi' || this.transferOptions.canWriteToErp;
    }

    /** The tooltip on a disabled `Direkt ans ERP senden` — a KEY the backend chose. */
    transferReasonKey(method: AdjustmentTransferMethod): string | null {
        return method === 'erpApi' && !this.transferOptions.canWriteToErp ? this.transferOptions.reasonKey : null;
    }

    /**
     * Whether NO accounting system is connected at all — a different statement from "connected but
     * cannot write", and the surface owes the user different words for it (§3a Regel 4, AC7).
     *
     * ⚠️ A disabled button with a tooltip is NOT enough here, which is what this view did before:
     * a tooltip appears on hover, so a keyboard or touch user reads three offered targets, finds the
     * first one dead, and is told nothing. "Nothing is connected" has to be ON the surface.
     *
     * `connectedName` is the discriminator and the only honest one available. A vendor without a
     * write API still HAS a name (that is the `no-erp-write` case, which keeps its tooltip);
     * `null` means there is no connection to name — and inventing one is the defect §3a exists to
     * prevent.
     */
    get isNothingConnected(): boolean {
        return !this.transferOptions.connectedName;
    }

    /** The transfer targets as menu entries — title, the line below it, and why one is unavailable. */
    get transferItems(): HamMenuItem[] {
        return this.transferTargets.map((method) => {
            const reasonKey = this.transferReasonKey(method);
            return {
                id: method,
                title: this.i18next.transform(this.transferTitleKey(method)),
                subtitle:
                    method === 'erpApi' && this.transferOptions.connectedName
                        ? this.transferOptions.connectedName
                        : this.i18next.transform(this.transferSubtitleKey(method)),
                disabled: !this.isTransferTargetEnabled(method),
                reason: reasonKey === null ? null : this.i18next.transform(reasonKey),
            };
        });
    }

    /** Nothing connected: SAID above the targets, not left to a tooltip on a dead entry (§3a rule 4). */
    get transferNotice(): string | null {
        return this.isNothingConnected && this.transferOptions.reasonKey
            ? this.i18next.transform(this.transferOptions.reasonKey)
            : null;
    }

    /** The years as menu entries; the shown one carries the check. */
    get yearItems(): HamMenuItem[] {
        return this.fiscalYears.map((year) => ({
            id: String(year),
            title: this.yearLabel(year),
            selected: year === this.selectedFiscalYear,
        }));
    }

    /** `aria-sort` — only the sorted column claims a direction. */
    ariaSort(key: AdjustmentSortKey): 'ascending' | 'descending' | 'none' {
        return ariaSortOf(this.sort, key);
    }

    sortDirection(key: AdjustmentSortKey): SortDirection | null {
        return directionOf(this.sort, key);
    }

    /** The header label sits where its column does: text left, amount right, the rest centred. */
    headerAlign(key: AdjustmentSortKey): 'start' | 'center' | 'end' {
        if (key === 'text') {
            return 'start';
        }
        return key === 'amount' ? 'end' : 'center';
    }

    /** A prior-year row's chip reads `Gebucht <Jahr>` (§10). */
    bookedYearOf(row: AdjustmentRow): number | null {
        return row.source === 'priorYear' ? this.selectedFiscalYear : null;
    }

    trackById(_index: number, row: { id: string }): string {
        return row.id;
    }

    /**
     * For the lists of PRIMITIVES — the pills, the columns, the transfer targets, the years.
     *
     * Its own function rather than reusing `trackById`: that one reads `.id` and would return
     * `undefined` for every entry of a string list, which makes Angular re-create the whole list on
     * every change detection instead of reusing it.
     */
    trackBySelf<T extends string | number>(_index: number, value: T): T {
        return value;
    }

    onFilterClick(key: AdjustmentFilterKey): void {
        this.filterChange.emit(key);
    }

    /** A new column starts ascending, the sorted one turns descending, a third click un-sorts. */
    onSortClick(key: AdjustmentSortKey): void {
        this.sortChange.emit(nextSort(this.sort, key));
    }

    /**
     * `2026 · aktuell` for the current year, the bare year otherwise.
     *
     * Built here rather than in the template because an `<option>` may not contain elements — the
     * previous version wrapped the suffix in a `<span>`, which every browser strips, so the marking
     * was invisible. The translated word is looked up through the same pipe the template would use.
     */
    yearLabel(year: number): string {
        if (year !== this.fiscalYears[0]) {
            return String(year);
        }

        return `${String(year)} · ${this.i18next.transform('abcp_adjustments_fiscal_year_current')}`;
    }

    onFiscalYearSelect(value: string): void {
        const year = Number(value);
        if (Number.isFinite(year)) {
            this.fiscalYearChange.emit(year);
        }
    }

    onRowClick(row: AdjustmentRow): void {
        this.adjustmentOpen.emit(row.id);
    }

    onCreateClick(): void {
        this.createRequested.emit();
    }

    onTransferTargetClick(id: string): void {
        const method = id as AdjustmentTransferMethod;
        if (!this.isTransferTargetEnabled(method)) {
            return;
        }
        this.transferRequested.emit(method);
    }

    onSearchResetClick(): void {
        this.searchReset.emit();
    }

    onRetryClick(): void {
        this.retryRequested.emit();
    }
}
